/**
 * Dashboard Component
 * Main executive dashboard displaying 7 financial metrics
 * Uses Custom Connections for automatic Xero authentication
 */

import { useMetrics } from '@/hooks/useMetrics';
import { useXeroAuth } from '@/hooks/useXeroAuth';
import { MetricCard } from './MetricCard';
import { CommentButton } from './CommentButton';
import { CashFlowChart } from './CashFlowChart';
import { InvoiceListModal } from './InvoiceListModal';
import type { ModalVariant } from './InvoiceListModal';
import { CashFlowDetailModal } from './CashFlowDetailModal';
import { ReconciliationPanel } from './ReconciliationPanel';
import { XeroConnectionChip, XeroReconnectCard } from './XeroConnectionStatus';
import { formatCurrency, formatRelativeTime, formatDays } from '@/utils/formatters';
import type { TopClient } from '@/types/metrics';
import './Dashboard.css';
import { useState, useCallback, useMemo } from 'react';

type ActiveModal =
  | { type: 'revenue' }
  | { type: 'outstanding' }
  | { type: 'client'; client: TopClient }
  | { type: 'cashflow' }
  | { type: 'overdue' }
  | { type: 'velocity' }
  | { type: 'unreconciled' }
  | null;

export function Dashboard() {
  const { data, loading, error, lastUpdated, refresh } = useMetrics();
  const { isAuthenticated, checkingAuth, connecting, connectError, expiresAt, connectToXero } = useXeroAuth();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  // Build query params for the current month
  const currentMonthParams = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return {
      type: 'ACCREC',
      status: 'SUBMITTED,AUTHORISED,PAID',
      dateFrom: `${year}-${month}-01`,
      dateTo: `${year}-${month}-${day}`,
      pageSize: '100',
    };
  }, []);

  // Modal configuration based on active modal type
  const modalConfig = useMemo((): {
    title: string;
    queryParams: Record<string, string>;
    variant: ModalVariant;
  } | null => {
    if (!activeModal) return null;

    switch (activeModal.type) {
      case 'revenue':
        return {
          title: 'Revenue This Month',
          queryParams: currentMonthParams,
          variant: 'default',
        };
      case 'outstanding':
        return {
          title: 'Outstanding Invoices',
          queryParams: { type: 'ACCREC', status: 'AUTHORISED', pageSize: '100' },
          variant: 'default',
        };
      case 'client':
        return {
          title: activeModal.client.name,
          queryParams: {
            contactID: activeModal.client.contactId,
            type: 'ACCREC',
            status: 'SUBMITTED,AUTHORISED,PAID',
            pageSize: '100',
          },
          variant: 'default',
        };
      case 'cashflow':
        return {
          title: 'Cash Flow Forecast',
          queryParams: { type: 'ACCREC', status: 'AUTHORISED', pageSize: '100' },
          variant: 'cashflow',
        };
      case 'overdue':
        return {
          title: 'Overdue Invoices',
          queryParams: { type: 'ACCREC', status: 'AUTHORISED', pageSize: '100' },
          variant: 'overdue',
        };
      case 'velocity':
        return {
          title: 'Payment Velocity',
          queryParams: { type: 'ACCREC', status: 'PAID', pageSize: '100' },
          variant: 'velocity',
        };
      case 'unreconciled':
        return {
          title: 'Unreconciled Transactions',
          queryParams: { isReconciled: 'false', dateFrom: '2025-07-01' },
          variant: 'bankTransactions',
        };
      default:
        return null;
    }
  }, [activeModal, currentMonthParams]);

  const closeModal = useCallback(() => setActiveModal(null), []);

  // Show loading while checking authentication or connecting
  if (checkingAuth || connecting) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>{checkingAuth ? 'Checking authentication...' : 'Connecting to Xero...'}</p>
      </div>
    );
  }

  // Connection lapsed — show the friendly, business-toned reconnect prompt (one click,
  // Custom Connections, no Xero login). Any underlying error is shown as a quiet detail.
  if (isAuthenticated === false && !connecting) {
    return (
      <XeroReconnectCard
        connecting={connecting}
        onRefresh={connectToXero}
        detail={connectError ?? undefined}
      />
    );
  }

  // Show error screen for API errors (not authentication issues)
  if (error && !error.includes('Authentication required')) {
    return (
      <div className="dashboard-error" role="alert" aria-live="assertive">
        <div className="dashboard-error__icon">⚠</div>
        <h2 className="dashboard-error__title">Unable to Load Dashboard</h2>
        <p className="dashboard-error__message">{error}</p>
        <button
          onClick={refresh}
          className="dashboard-error__button"
          aria-label="Retry loading dashboard"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header__content">
          <h1 className="dashboard-header__title">Executive Dashboard</h1>
          <div className="dashboard-header__status">
            <XeroConnectionChip expiresAt={expiresAt} connecting={connecting} onRefresh={connectToXero} />
          </div>
          {lastUpdated && (
            <p className="dashboard-header__updated">
              Last updated {formatRelativeTime(lastUpdated)}
            </p>
          )}
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="dashboard-header__refresh"
          aria-label="Refresh metrics"
        >
          <span className={loading ? 'loading-spinner' : ''}>↻</span>
          Refresh
        </button>
      </header>

      {/* Metrics Grid */}
      <div className="dashboard-grid">
        {/* 1. Revenue This Month */}
        <MetricCard
          title="Revenue This Month"
          value={formatCurrency(data?.monthlyRevenue?.total ?? 0)}
          subtitle={`${data?.monthlyRevenue?.count ?? 0} invoices`}
          icon="💰"
          loading={loading}
          variant="success"
          onClick={() => setActiveModal({ type: 'revenue' })}
          headerAction={<CommentButton targetType="metric" targetId="monthly-revenue" />}
        >
          <div className="breakdown">
            <div className="breakdown__item">
              <span className="breakdown__label">Paid</span>
              <span className="breakdown__value">
                {formatCurrency(data?.monthlyRevenue?.breakdown?.paid ?? 0)}
              </span>
            </div>
            <div className="breakdown__item">
              <span className="breakdown__label">Invoiced</span>
              <span className="breakdown__value">
                {formatCurrency(data?.monthlyRevenue?.breakdown?.invoiced ?? 0)}
              </span>
            </div>
          </div>
        </MetricCard>

        {/* 2. Outstanding Invoices */}
        <MetricCard
          title="Outstanding Invoices"
          value={`${formatCurrency(data?.outstandingInvoices?.total ?? 0)}`}
          subtitle={`${data?.outstandingInvoices?.count ?? 0} invoices pending (inc. GST)`}
          icon="📋"
          loading={loading}
          onClick={() => setActiveModal({ type: 'outstanding' })}
          headerAction={<CommentButton targetType="metric" targetId="outstanding-invoices" />}
        >
          <div className="aging">
            <div className="aging__item">
              <span className="aging__label">0-30 days</span>
              <span className="aging__value">
                {formatCurrency(data?.outstandingInvoices?.aging?.days0to30?.total ?? 0)}
              </span>
            </div>
            <div className="aging__item">
              <span className="aging__label">31-60 days</span>
              <span className="aging__value">
                {formatCurrency(data?.outstandingInvoices?.aging?.days31to60?.total ?? 0)}
              </span>
            </div>
            <div className="aging__item">
              <span className="aging__label">61-90 days</span>
              <span className="aging__value">
                {formatCurrency(data?.outstandingInvoices?.aging?.days61to90?.total ?? 0)}
              </span>
            </div>
            <div className="aging__item">
              <span className="aging__label">90+ days</span>
              <span className="aging__value">
                {formatCurrency(data?.outstandingInvoices?.aging?.days90Plus?.total ?? 0)}
              </span>
            </div>
          </div>
        </MetricCard>

        {/* 3. Top 5 Clients */}
        <MetricCard
          title="Top 5 Clients"
          value={data?.topClients?.length ?? 0}
          subtitle="by revenue (FY26)"
          icon="⭐"
          loading={loading}
          headerAction={<CommentButton targetType="metric" targetId="top-clients" />}
        >
          <div className="top-clients">
            {data?.topClients.map((client: TopClient, index: number) => (
              <button
                key={client.contactId}
                className="top-clients__item top-clients__item--clickable"
                onClick={() => setActiveModal({ type: 'client', client })}
                aria-label={`View invoices for ${client.name}`}
              >
                <span className="top-clients__rank">#{index + 1}</span>
                <div className="top-clients__info">
                  <span className="top-clients__name">{client.name}</span>
                  <span className="top-clients__revenue">
                    {formatCurrency(client.revenue)}
                  </span>
                </div>
                <span className="top-clients__chevron" aria-hidden="true">&rsaquo;</span>
              </button>
            ))}
            {(!data?.topClients || data.topClients.length === 0) && (
              <p className="top-clients__empty">No client data available</p>
            )}
          </div>
        </MetricCard>

        {/* 4. Cash Flow Forecast */}
        <MetricCard
          title="Cash Flow Forecast"
          value={formatCurrency(data?.cashFlowForecast?.day90 ?? 0)}
          subtitle="90-day projection"
          icon="📈"
          loading={loading}
          onClick={() => setActiveModal({ type: 'cashflow' })}
          headerAction={<CommentButton targetType="metric" targetId="cash-flow-forecast" />}
        >
          {data?.cashFlowForecast && (
            <CashFlowChart data={data.cashFlowForecast} loading={loading} />
          )}
        </MetricCard>

        {/* 5. Overdue Invoices */}
        <MetricCard
          title="Overdue Invoices"
          value={formatCurrency(data?.overdueInvoices?.total ?? 0)}
          subtitle={`${data?.overdueInvoices?.count ?? 0} invoices overdue (inc. GST)`}
          icon="⚠"
          loading={loading}
          variant={
            (data?.overdueInvoices?.count ?? 0) > 0 ? 'warning' : 'default'
          }
          onClick={() => setActiveModal({ type: 'overdue' })}
          headerAction={<CommentButton targetType="metric" targetId="overdue-invoices" />}
        >
          {data?.overdueInvoices?.oldestDate && (
            <p className="overdue-info">
              Oldest overdue:{' '}
              {formatRelativeTime(data.overdueInvoices.oldestDate)}
            </p>
          )}
        </MetricCard>

        {/* 6. Payment Velocity */}
        <MetricCard
          title="Payment Velocity"
          value={formatDays(data?.paymentVelocity?.weightedAverageDays ?? data?.paymentVelocity?.averageDays ?? 0)}
          subtitle="weighted avg time to payment"
          icon="⚡"
          loading={loading}
          onClick={() => setActiveModal({ type: 'velocity' })}
          headerAction={<CommentButton targetType="metric" targetId="payment-velocity" />}
        >
          <div className="velocity">
            <div className="velocity__item">
              <span className="velocity__label">Simple Avg</span>
              <span className="velocity__value">
                {formatDays(data?.paymentVelocity?.averageDays ?? 0)}
              </span>
            </div>
            <div className="velocity__item">
              <span className="velocity__label">Median</span>
              <span className="velocity__value">
                {formatDays(data?.paymentVelocity?.medianDays ?? 0)}
              </span>
            </div>
            <div className="velocity__item">
              <span className="velocity__label">Sample size</span>
              <span className="velocity__value">
                {data?.paymentVelocity?.sampleSize ?? 0} invoices
              </span>
            </div>
          </div>
        </MetricCard>

        {/* 7. Unreconciled Totals */}
        <MetricCard
          title="Unreconciled"
          value={formatCurrency(
            (data?.unreconciled?.income ?? 0) + (data?.unreconciled?.outgoings ?? 0)
          )}
          subtitle={`${(data?.unreconciled?.incomeCount ?? 0) + (data?.unreconciled?.outgoingsCount ?? 0)} transactions`}
          icon="🔄"
          loading={loading}
          onClick={() => setActiveModal({ type: 'unreconciled' })}
          headerAction={<CommentButton targetType="metric" targetId="unreconciled" />}
        >
          <div className="breakdown">
            <div className="breakdown__item">
              <span className="breakdown__label">Income</span>
              <span className="breakdown__value">
                {formatCurrency(data?.unreconciled?.income ?? 0)}
              </span>
            </div>
            <div className="breakdown__item">
              <span className="breakdown__label">Outgoings</span>
              <span className="breakdown__value">
                {formatCurrency(data?.unreconciled?.outgoings ?? 0)}
              </span>
            </div>
          </div>
        </MetricCard>
      </div>

      {/* Drill-Down Modals */}
      {activeModal?.type === 'cashflow' && (
        <CashFlowDetailModal onClose={closeModal} />
      )}
      {modalConfig && activeModal?.type !== 'cashflow' && (
        <InvoiceListModal
          title={modalConfig.title}
          queryParams={modalConfig.queryParams}
          variant={modalConfig.variant}
          onClose={closeModal}
        >
          {activeModal?.type === 'unreconciled' && (
            <ReconciliationPanel onReconciled={refresh} />
          )}
        </InvoiceListModal>
      )}
    </div>
  );
}
